import React, { useState } from 'react';
import { Button, Form, Input, Select, Switch, Row, Col, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { QualifyingFlowOption, QualifyingFlowQuestion, QuestionType } from '../../../../dto/qualifying-flow-models';

type QuestionFormProps = {
  question: QualifyingFlowQuestion;
  onSubmit: (question: QualifyingFlowQuestion) => void;
  onCancel: () => void;
};

export const QualifyingQuestionForm = ({ question, onSubmit, onCancel }: QuestionFormProps) => {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState<QualifyingFlowQuestion>({ ...question, options: [...question.options] });

  const setField = (updates: Partial<QualifyingFlowQuestion>) => {
    setCurrent((prev) => ({ ...prev, ...updates }));
  };

  const addDropdownOption = () => {
    const idx = current.options.length;
    const newOption: QualifyingFlowOption = {
      feId: crypto.randomUUID(),
      key: `option_${idx}`,
      text: { de: '', en: '' },
    };
    setField({ options: [...current.options, newOption] });
  };

  const updateDropdownOption = (feId: string, updates: Partial<QualifyingFlowOption>) => {
    setField({
      options: current.options.map((o) => (o.feId === feId ? { ...o, ...updates } : o)),
    });
  };

  const removeDropdownOption = (feId: string) => {
    setField({ options: current.options.filter((o) => o.feId !== feId) });
  };

  const handleTypeChange = (type: QuestionType) => {
    setCurrent((prev) => ({ ...prev, questionType: type, options: [], allowEmpty: false }));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      onSubmit(current);
    } catch {}
  };

  return (
    <div style={{ padding: '16px' }}>
      <Form form={form} layout="vertical">
        <Form.Item
          label="Question Key"
          name="questionKey"
          initialValue={current.questionKey}
          rules={[{ required: true, message: 'Question key is required' }]}
        >
          <Input
            placeholder="e.g. installation_timeline"
            value={current.questionKey}
            onChange={(e) => setField({ questionKey: e.target.value })}
          />
        </Form.Item>

        <Form.Item
          label="Question Text (DE)"
          name="questionTextDe"
          initialValue={current.questionText?.de ?? ''}
          rules={[{ required: true, message: 'German question text is required' }]}
        >
          <Input.TextArea
            rows={2}
            value={current.questionText?.de ?? ''}
            onChange={(e) => setField({ questionText: { ...current.questionText, de: e.target.value } })}
          />
        </Form.Item>

        <Form.Item
          label="Question Text (EN)"
          name="questionTextEn"
          initialValue={current.questionText?.en ?? ''}
          rules={[{ required: true, message: 'English question text is required' }]}
        >
          <Input.TextArea
            rows={2}
            value={current.questionText?.en ?? ''}
            onChange={(e) => setField({ questionText: { ...current.questionText, en: e.target.value } })}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Question Type" name="questionType" initialValue={current.questionType}>
              <Select value={current.questionType} onChange={handleTypeChange}>
                <Select.Option value={QuestionType.Dropdown}>Dropdown</Select.Option>
                <Select.Option value={QuestionType.NumberField}>Number Field</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Active">
              <Switch checked={current.isActive} onChange={(val) => setField({ isActive: val })} />
            </Form.Item>
          </Col>
        </Row>

        {current.questionType === QuestionType.Dropdown && (
          <>
            <Divider orientation="left">Answer Options</Divider>
            {current.options.map((opt) => (
              <Row key={opt.feId} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                <Col span={6}>
                  <Input
                    placeholder="Key (e.g. option_0)"
                    value={opt.key}
                    onChange={(e) => updateDropdownOption(opt.feId, { key: e.target.value })}
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="Label (DE)"
                    value={opt.text?.de ?? ''}
                    onChange={(e) =>
                      updateDropdownOption(opt.feId, { text: { ...opt.text, de: e.target.value } })
                    }
                  />
                </Col>
                <Col span={8}>
                  <Input
                    placeholder="Label (EN)"
                    value={opt.text?.en ?? ''}
                    onChange={(e) =>
                      updateDropdownOption(opt.feId, { text: { ...opt.text, en: e.target.value } })
                    }
                  />
                </Col>
                <Col span={2}>
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    size="small"
                    onClick={() => removeDropdownOption(opt.feId)}
                  />
                </Col>
              </Row>
            ))}
            <Button icon={<PlusOutlined />} onClick={addDropdownOption} style={{ marginTop: 8 }}>
              Add Option
            </Button>
          </>
        )}

        {current.questionType === QuestionType.NumberField && (
          <>
            <Divider orientation="left">Number Field Settings</Divider>
            <Form.Item label="Allow Empty (unanswered)">
              <Switch
                checked={current.allowEmpty ?? false}
                onChange={(val) => setField({ allowEmpty: val })}
              />
            </Form.Item>
          </>
        )}
      </Form>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </div>
  );
};
